import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router';
import {
  CheckCircle,
  XCircle,
  ExternalLink,
  Plus,
  PencilLine,
  Settings,
  FilePenLine,
  PlayCircle,
  Flag,
  Users,
  Layers,
  CalendarDays,
  DollarSign,
} from 'lucide-react';
import { updateProject, addCommentToEntity, updateCommentInEntity, deleteCommentFromEntity, addReactionToComment } from '../store';
import { Project, ProjectStatus, Comment, CostItem, ChangeLog, Task } from '../types';
import ProjectChat from '../components/ProjectChat';
import { MarkdownDescription } from '../components/MarkdownDescription';
import { CommentSection } from '../components/CommentSection';
import SurfaceCard from '../components/SurfaceCard';
import { SummaryStatCard } from '../components/StatCards';
import { NewsFeedComposer, NewsFeedPostCard } from '../components/NewsFeedShared';
import UserContactLinks from '../components/UserContactLinks';
import { useApp } from '../providers/AppProvider';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { toast } from 'sonner';
import { generateId } from '../utils/idGenerator';
import { getUserProfilePath } from '../utils/userProfile';
import {
  canEditProjectFinance,
  canManageProjectSettings,
  canViewProjectFinance,
  getProjectMemberRole,
  getProjectTeamUsers,
} from '../utils/projectAccess';
import { assignCostItemCodes } from '../utils/costItems';
import { useNewsFeed } from '../hooks/useNewsFeed';
import { APP_NATIVE_FIELD_CLASS } from '../constants/formControls';
import { getProjectStatusActionColors, getProjectStatusBadgeColors } from '../constants/colorScheme';
import { normalizeEntityId, generateAutoEntityId } from '../utils/entityIds';

export default function ProjectDetails() {
  const { projectId } = useParams();
  const { state, loading, reload } = useApp();
  const { projects, tasks } = useOrganizationData(state) as {
    projects: Project[];
    tasks: Task[];
  };

  const project = useMemo(() => {
    return projects.find(
      p => String(p.id).toLowerCase() === String(projectId).toLowerCase()
    );
  }, [projects, projectId]);

  const [costDrafts, setCostDrafts] = useState<CostItem[]>([]);
  const fallbackNewsFeedPosts = useMemo(
    () => (state?.newsFeedPosts ?? []).filter((post) => post.projectId === projectId && post.scope === 'project'),
    [projectId, state?.newsFeedPosts]
  );
  const {
    posts: newsFeedPosts,
    loading: newsFeedLoading,
    createPost: handleCreateNewsPost,
    editPost: handleEditNewsPost,
    deletePost: handleDeleteNewsPost,
    commentOnPost: handleCommentOnNewsPost,
    reactToPost: handleReactToNewsPost,
  } = useNewsFeed({
    organizationId: state?.currentOrganization?.id,
    projectId,
    currentUser: state?.currentUser,
    fallbackPosts: fallbackNewsFeedPosts,
  });

  useEffect(() => {
    const costs = project?.costs || [];
    const sortedCosts = [...costs].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    setCostDrafts(assignCostItemCodes(sortedCosts));
  }, [project?.costs, project?.updatedAt]);

  // 1. Show loading spinner while fetching
  if (loading && !project) {
    return <div className="p-8 text-center text-muted-foreground">Loading project...</div>;
  }


  if (!project) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <p className="mb-4 text-muted-foreground">Project not found</p>
          <Link to="/app/projects" className="text-primary hover:text-primary/80">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  if (!state?.currentUser) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <p className="text-muted-foreground">User context not loaded</p>
        </div>
      </div>
    );
  }

  const currentUser = state.currentUser;
  const currentProjectRole =
    getProjectMemberRole(project, currentUser.id, currentUser.email) || currentUser.role;
  const canManageProject = canManageProjectSettings(currentUser, project);
  const canViewFinance = canViewProjectFinance(currentUser, project);
  const canEditFinance = canEditProjectFinance(currentUser, project);

  const handleStatusChange = async (newStatus: ProjectStatus) => {
    if (!canManageProject) {
      toast.error('Only project owners and admins can change project status');
      return;
    }

    try {
      await updateProject(project.id, {
        status: newStatus,
        updatedAt: new Date().toISOString(),
        ...(newStatus === 'Approved' ? { approvedAt: new Date().toISOString() } : {}),
      });

      if (currentUser.email) {
        await reload(currentUser.email);
      }

      toast.success('Project status updated');
    } catch (error) {
      console.error('[ProjectDetails] Failed to update status:', error);
      toast.error('Failed to update project status');
    }
  };

  const projectTasks = tasks.filter((t) => t.projectId === project.id);
  const projectSprints = (state?.sprints ?? []).filter((s) => s.projectId === project.id);
  const projectEpics = (state?.epics ?? []).filter((e) => e.projectId === project.id);
  const completedTasks = projectTasks.filter((t) => t.status === 'Completed').length;
  const projectBudgetTotal = costDrafts.reduce((sum, cost) => sum + Number(cost.amount || 0), 0);


  const teamMemberRows = useMemo(() => {
    const normalize = (value?: string | null) => String(value || '').trim().toLowerCase();
    const users = state?.users || [];
    const teams = state?.teams || [];
    const resolvedProjectUsers = getProjectTeamUsers(project, users, teams);

    const userByIdentity = new Map<string, (typeof users)[number]>();
    users.forEach((user) => {
      const normalizedId = normalize(user.id);
      const normalizedEmail = normalize(user.email);

      if (normalizedId) {
        userByIdentity.set(normalizedId, user);
      }

      if (normalizedEmail) {
        userByIdentity.set(normalizedEmail, user);
      }
    });

    const rowsByKey = new Map<
      string,
      {
        name: string;
        email: string;
        role: string;
        phoneNumber?: string;
        linkedinUrl?: string;
        githubUrl?: string;
        facebookUrl?: string;
        profilePath?: string;
      }
    >();

    (project.teamMembers || []).forEach((member, index) => {
      const normalizedMemberId = normalize(member.userId);
      const matchedUser = userByIdentity.get(normalizedMemberId);

      const rawIdentifier = String(member.userId || '').trim();
      const fallbackName = rawIdentifier.includes('@')
        ? rawIdentifier.split('@')[0]
        : rawIdentifier || 'Unknown User';
      const fallbackEmail = rawIdentifier.includes('@') ? rawIdentifier : 'No email available';

      const dedupeKey =
        normalize(matchedUser?.email) ||
        normalize(matchedUser?.id) ||
        `${normalizedMemberId}-${index}`;

      rowsByKey.set(dedupeKey, {
        name: matchedUser?.name || fallbackName,
        email: matchedUser?.email || fallbackEmail,
        role: String(member.role || 'team_member').replace(/_/g, ' '),
        phoneNumber: matchedUser?.phoneNumber,
        linkedinUrl: matchedUser?.linkedinUrl,
        githubUrl: matchedUser?.githubUrl,
        facebookUrl: matchedUser?.facebookUrl,
        profilePath: matchedUser ? getUserProfilePath(matchedUser, currentUser) : undefined,
      });
    });

    resolvedProjectUsers.forEach((user) => {
      const dedupeKey = normalize(user.email) || normalize(user.id);
      if (!dedupeKey || rowsByKey.has(dedupeKey)) {
        return;
      }

      const projectRole = getProjectMemberRole(project, user.id, user.email) || 'team_member';
      rowsByKey.set(dedupeKey, {
        name: user.name || 'Unknown User',
        email: user.email || 'No email available',
        role: projectRole.replace(/_/g, ' '),
        phoneNumber: user.phoneNumber,
        linkedinUrl: user.linkedinUrl,
        githubUrl: user.githubUrl,
        facebookUrl: user.facebookUrl,
        profilePath: getUserProfilePath(user, currentUser),
      });
    });

    return Array.from(rowsByKey.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [currentUser, project, state?.teams, state?.users]);

  const formatRole = (value: string) =>
    value
      .replace(/_/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

  const projectCostChangeLogs =
    (((project.metadata || {}) as Record<string, any>).costChangeLogs as ChangeLog[] | undefined) || [];

  const managementActions = useMemo(
    () => [
      {
        label: 'Edit Project',
        description: 'Update name, description, team members, leads, and access rules.',
        href: `/app/projects/${project.id}/settings`,
        icon: Settings,
      },
      {
        label: 'Manage Requirements',
        description: 'Refine scope, acceptance criteria, and linked requirement details.',
        href: `/app/projects/${project.id}/requirements`,
        icon: FilePenLine,
      },
    ],
    [project.id]
  );

  const statusActions = useMemo(() => {
    if (!canManageProject) {
      return [] as Array<{
        label: string;
        nextStatus: ProjectStatus;
        icon: typeof CheckCircle;
        className: string;
      }>;
    }

    switch (project.status) {
      case 'Draft':
        return [
          {
            label: 'Submit for Review',
            nextStatus: 'Under Review' as ProjectStatus,
            icon: PencilLine,
            className: getProjectStatusActionColors('Under Review'),
          },
        ];
      case 'Under Review':
        return [
          {
            label: 'Approve Project',
            nextStatus: 'Approved' as ProjectStatus,
            icon: CheckCircle,
            className: getProjectStatusActionColors('Approved'),
          },
          {
            label: 'Reject Project',
            nextStatus: 'Rejected' as ProjectStatus,
            icon: XCircle,
            className: getProjectStatusActionColors('Rejected'),
          },
        ];
      case 'Approved':
        return [
          {
            label: 'Start Project',
            nextStatus: 'In Progress' as ProjectStatus,
            icon: PlayCircle,
            className: getProjectStatusActionColors('In Progress'),
          },
        ];
      case 'Rejected':
        return [
          {
            label: 'Resubmit for Review',
            nextStatus: 'Under Review' as ProjectStatus,
            icon: PencilLine,
            className: getProjectStatusActionColors('Under Review'),
          },
        ];
      case 'In Progress':
        return [
          {
            label: 'Mark Completed',
            nextStatus: 'Completed' as ProjectStatus,
            icon: Flag,
            className: getProjectStatusActionColors('Completed'),
          },
        ];
      default:
        return [];
    }
  }, [canManageProject, project.status]);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-3 lg:pt-4 pb-6">
      <SurfaceCard className="mb-5 overflow-hidden border border-border bg-card p-4 shadow-sm sm:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${getProjectStatusBadgeColors(project.status)}`}>
                {project.status}
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/60 px-3 py-1 text-xs text-muted-foreground">
                Your role: {formatRole(currentProjectRole)}
              </span>
              {canManageProject && (
                <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${getProjectStatusBadgeColors('Approved')}`}>
                  Admin / Owner controls enabled
                </span>
              )}
            </div>
            <h1 className="mb-1 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">{project.name}</h1>
            <div className="max-w-2xl text-sm leading-5 text-muted-foreground">
              {project.description ? (
                <MarkdownDescription content={project.description} className="text-muted-foreground" />
              ) : (
                <p className="text-muted-foreground">No project description yet.</p>
              )}
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <SummaryStatCard
                icon={<CheckCircle className="w-5 h-5" />}
                label="Tasks Done"
                value={`${completedTasks} / ${projectTasks.length}`}
                color="green"

              />
              <SummaryStatCard
                icon={<Users className="w-5 h-5" />}
                label="Team"
                value={`${teamMemberRows.length} member${teamMemberRows.length !== 1 ? 's' : ''}`}
                color="blue"

              />
              <SummaryStatCard
                icon={<Layers className="w-5 h-5" />}
                label="Epics"
                value={`${projectEpics.length}`}
                color="purple"

              />
              <SummaryStatCard
                icon={<CalendarDays className="w-5 h-5" />}
                label="Sprints"
                value={`${projectSprints.length}`}
                color="orange"

              />
              {project.targetEndDate && (
                <SummaryStatCard
                  icon={<Flag className="w-5 h-5" />}
                  label="Target End"
                  value={new Date(project.targetEndDate).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                  color="purple"
  
                />
              )}
              {canViewFinance && costDrafts.length > 0 && (
                <SummaryStatCard
                  icon={<DollarSign className="w-5 h-5" />}
                  label="Budget"
                  value={`$${projectBudgetTotal.toLocaleString()}`}
                  color="green"
  
                />
              )}
            </div>
          </div>

          {canManageProject ? (
            <div className="w-full rounded-xl border border-border bg-card p-3.5 shadow-sm lg:w-[22rem]">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
                <PencilLine className="h-4 w-4 text-primary" />
                Project Controls
              </div>
              <p className="mb-3 text-sm leading-5 text-muted-foreground">
                Admins and owners can update project settings, refine requirements, and move the project through its workflow.
              </p>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
                {managementActions.map((action) => {
                  const Icon = action.icon;
                  return (
                    <Link
                      key={action.href}
                      to={action.href}
                      className="group rounded-2xl border border-border bg-muted/40 p-3 transition-all hover:border-primary/40 hover:bg-accent"
                    >
                      <div className="flex items-start gap-3">
                        <div className="rounded-xl bg-background p-2 text-primary shadow-sm ring-1 ring-border transition-colors group-hover:bg-accent group-hover:text-primary">
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-foreground">{action.label}</div>
                          <div className="mt-1 text-xs leading-5 text-muted-foreground">{action.description}</div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              {statusActions.length > 0 && (
                <div className="mt-3 border-t border-border pt-3">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Workflow Actions
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {statusActions.map((action) => {
                      const Icon = action.icon;
                      return (
                        <button
                          key={action.nextStatus}
                          onClick={() => handleStatusChange(action.nextStatus)}
                          className={`inline-flex min-h-10 items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${action.className}`}
                        >
                          <Icon className="h-4 w-4" />
                          {action.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-xl border border-border bg-card px-3 py-2.5 text-sm text-muted-foreground shadow-sm">
              Project edits are limited to project admins and owners.
            </div>
          )}
        </div>
      </SurfaceCard>

      <SurfaceCard className="mb-5 p-4 sm:p-6">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-foreground">Project Proposal</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Review the current proposal and use the controls above to move it forward.
          </p>
        </div>
        <div className="prose max-w-none">
          {project.proposal ? (
            <MarkdownDescription content={project.proposal} className="text-muted-foreground" />
          ) : (
            <p className="text-muted-foreground italic">No proposal content yet</p>
          )}
        </div>
      </SurfaceCard>

      {/* Cost Breakdown - Now on dedicated page at /app/projects/:projectId/costs */}
      {/* Team Members */}
      <SurfaceCard className="mb-5 p-4 sm:p-6">
        <h2 className="mb-4 text-xl text-foreground">Team Members</h2>
        {teamMemberRows.length === 0 ? (
          <p className="italic text-muted-foreground">No team members assigned yet</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {teamMemberRows.map((member, index) => {
              return (
                <div key={`${member.email}-${member.role}-${index}`} className="flex items-center justify-between rounded-lg border border-border p-3">
                  <div>
                    <p className="text-foreground">{member.name}</p>
                    <p className="text-sm text-muted-foreground">{member.email}</p>
                    <UserContactLinks user={member} className="mt-1" />
                    {member.profilePath && (
                      <Link
                        to={member.profilePath}
                        className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline"
                      >
                        View profile
                      </Link>
                    )}
                  </div>
                  <span className="rounded-full bg-muted px-3 py-1 text-sm capitalize text-foreground">
                    {member.role}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </SurfaceCard>

      {/* External Links */}
      {(project.externalLinks ?? []).length > 0 && (
        <SurfaceCard className="mb-5 p-4 sm:p-6">
          <h2 className="mb-4 text-xl text-foreground">External Links</h2>
          <div className="space-y-2">
            {project.externalLinks.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-lg border border-border p-3 hover:bg-accent"
              >
                <ExternalLink className="h-4 w-4 text-muted-foreground" />
                <span className="text-foreground">{link.type}</span>
                <span className="flex-1 truncate text-muted-foreground">{link.url}</span>
              </a>
            ))}
          </div>
        </SurfaceCard>
      )}

      {/* Project Chat */}
      <div className="mb-6">
        <ProjectChat projectId={project.id} projectName={project.name} />
      </div>

      {/* Comment Section */}
      <SurfaceCard className="mb-5 p-4 sm:p-6">
        <CommentSection
          comments={project.comments || []}
          currentUserId={state.currentUser.id}
          currentUserName={state.currentUser.name}
          onAddComment={async (content, parentCommentId, _includeInStandup, _includeInProjectFeed, _includeInOrgFeed, attachments) => {
            try {
              const newComment: Comment = {
                id: generateId('comment', project.id),
                content,
                createdBy: state.currentUser.id,
                createdByName: state.currentUser.name,
                createdAt: new Date().toISOString(),
                parentCommentId,
                attachments,
              };
              await addCommentToEntity('project', project.id, newComment);
              toast.success('Comment added');
            } catch (error) {
              console.error('[ProjectDetails] Failed to add comment:', error);
              toast.error('Failed to add comment');
            }
          }}
          onEditComment={async (commentId, newContent, attachments) => {
            try {
              const updates: Partial<Comment> = { content: newContent };
              if (attachments) {
                updates.attachments = attachments;
              }

              await updateCommentInEntity('project', project.id, commentId, updates);
              toast.success('Comment updated');
            } catch (error) {
              console.error('[ProjectDetails] Failed to update comment:', error);
              toast.error('Failed to update comment');
            }
          }}
          onDeleteComment={async (commentId) => {
            try {
              await deleteCommentFromEntity('project', project.id, commentId);
              toast.success('Comment deleted');
            } catch (error) {
              console.error('[ProjectDetails] Failed to delete comment:', error);
              toast.error('Failed to delete comment');
            }
          }}
          onAddReaction={async (commentId, emoji) => {
            try {
              await addReactionToComment('project', project.id, commentId, state.currentUser.id, emoji);
            } catch (error) {
              console.error('[ProjectDetails] Failed to add reaction:', error);
              toast.error('Failed to update reaction');
            }
          }}
          placeholder="Share your thoughts about this project..."
        />
      </SurfaceCard>

      <SurfaceCard className="mb-5 border border-border bg-card p-4 sm:p-6">
        <div className="mb-5 flex items-center gap-2">
          <div className="h-1 w-8 rounded-full bg-primary"></div>
          <h2 className="text-2xl font-semibold text-foreground">Project News Feed</h2>
        </div>

        <div className="mb-6">
          <NewsFeedComposer
            currentUserName={state.currentUser.name}
            currentUserAvatar={state.currentUser.avatar}
            fallbackAvatarSrc={project.logo}
            placeholder="Share an update with the project team..."
            onSubmit={handleCreateNewsPost}
            successMessage="Update shared!"
            errorMessage="Failed to share update"
          />
        </div>

        <div className="space-y-4">
          {newsFeedLoading ? (
            <div className="py-8 text-center text-muted-foreground">Loading news feed...</div>
          ) : newsFeedPosts.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <p className="mb-2">No updates yet</p>
              <p className="text-sm">Be the first to share an update about this project</p>
            </div>
          ) : (
            newsFeedPosts.map((post) => (
              <NewsFeedPostCard
                key={post.id}
                post={post}
                currentUserId={state.currentUser.id}
                currentUserName={state.currentUser.name}
                currentUserAvatar={state.currentUser.avatar}
                onEdit={handleEditNewsPost}
                onDelete={handleDeleteNewsPost}
                onComment={handleCommentOnNewsPost}
                onReact={handleReactToNewsPost}
              />
            ))
          )}
        </div>
      </SurfaceCard>
    </div>
  );
}